import React from "react";
import { AlertCircle, AlertTriangle, Info, TrendingUp } from "lucide-react";
import { cn } from "utils/cn";

const levelConfig = {
  critical: {
    icon: AlertCircle,
    card: "bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-800",
    badge: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
    iconColor: "text-red-500",
  },
  warning: {
    icon: AlertTriangle,
    card: "bg-yellow-50 border-yellow-200 dark:bg-yellow-950/20 dark:border-yellow-800",
    badge: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
    iconColor: "text-yellow-500",
  },
  info: {
    icon: Info,
    card: "bg-blue-50 border-blue-200 dark:bg-blue-950/20 dark:border-blue-800",
    badge: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
    iconColor: "text-blue-500",
  },
};

export default function RegionalAlertPanel({ alerts = [], regionName }) {
  const summary = alerts.reduce(
    (acc, alert) => {
      acc[alert.level] = (acc[alert.level] || 0) + 1;
      return acc;
    },
    { critical: 0, warning: 0, info: 0 },
  );

  const overviewAlert = alerts.find((alert) => alert.category === "Regional Overview");
  const issueAlerts = alerts.filter(
    (alert) => alert.category !== "Regional Overview" && alert.category !== "Top Performer",
  );
  const highlightAlerts = alerts.filter((alert) => alert.category === "Top Performer");

  if (alerts.length === 0) {
    return (
      <div className="metric-card mt-6">
        <div className="flex items-center gap-2 mb-4">
          <AlertCircle className="h-5 w-5 text-accent" />
          <h3 className="font-semibold text-sm">Regional Alerts & Notifications</h3>
        </div>
        <div className="rounded-lg border border-dashed border-border bg-muted/30 px-4 py-8 text-center text-sm text-muted-foreground">
          No alerts to report for {regionName || "this region"}.
        </div>
      </div>
    );
  }

  return (
    <div className="metric-card mt-6">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-accent" />
          <div>
            <h3 className="font-semibold text-sm">Regional Alerts & Notifications</h3>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {alerts.length} report item{alerts.length === 1 ? "" : "s"} for {regionName || "this region"}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {summary.critical > 0 && (
            <span className={cn("rounded-full px-2.5 py-1 text-[11px] font-medium", levelConfig.critical.badge)}>
              {summary.critical} critical
            </span>
          )}
          {summary.warning > 0 && (
            <span className={cn("rounded-full px-2.5 py-1 text-[11px] font-medium", levelConfig.warning.badge)}>
              {summary.warning} warning
            </span>
          )}
          {summary.info > 0 && (
            <span className={cn("rounded-full px-2.5 py-1 text-[11px] font-medium", levelConfig.info.badge)}>
              {summary.info} info
            </span>
          )}
        </div>
      </div>

      {overviewAlert && (
        <div className={cn("mb-4 rounded-lg border p-4", levelConfig[overviewAlert.level]?.card || levelConfig.info.card)}>
          <div className="flex items-start gap-3">
            <TrendingUp className={cn("mt-0.5 h-4 w-4 shrink-0", levelConfig[overviewAlert.level]?.iconColor)} />
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-semibold">{overviewAlert.title}</p>
                {overviewAlert.metric && (
                  <span className="rounded-full bg-background/80 px-2 py-0.5 text-[11px] font-medium">
                    {overviewAlert.metric}
                  </span>
                )}
              </div>
              <p className="mt-1 text-sm text-muted-foreground">{overviewAlert.message}</p>
            </div>
          </div>
        </div>
      )}

      {issueAlerts.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Districts Requiring Attention
          </h4>
          {issueAlerts.map((alert) => {
            const config = levelConfig[alert.level] || levelConfig.info;
            const Icon = config.icon;

            return (
              <div key={alert.id} className={cn("rounded-lg border p-4", config.card)}>
                <div className="flex items-start gap-3">
                  <Icon className={cn("mt-0.5 h-4 w-4 shrink-0", config.iconColor)} />
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      {alert.category && (
                        <span className="rounded-full bg-background/70 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                          {alert.category}
                        </span>
                      )}
                      {alert.metric && (
                        <span className="text-[11px] font-medium text-muted-foreground">{alert.metric}</span>
                      )}
                    </div>
                    <p className="mt-1 text-sm font-medium">{alert.title || alert.message}</p>
                    {alert.title && alert.message && (
                      <p className="mt-1 text-sm text-muted-foreground">{alert.message}</p>
                    )}
                    {alert.recommendation && (
                      <p className="mt-2 text-xs text-muted-foreground">
                        <span className="font-medium text-foreground">Recommended action:</span> {alert.recommendation}
                      </p>
                    )}
                    {alert.location && (
                      <p className="mt-1 text-xs text-muted-foreground">{alert.location}</p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {highlightAlerts.length > 0 && (
        <div className="mt-4 space-y-3">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Top Performers
          </h4>
          {highlightAlerts.map((alert) => {
            const config = levelConfig.info;
            const Icon = config.icon;

            return (
              <div key={alert.id} className={cn("rounded-lg border p-4", config.card)}>
                <div className="flex items-start gap-3">
                  <Icon className={cn("mt-0.5 h-4 w-4 shrink-0", config.iconColor)} />
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-background/70 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                        {alert.category}
                      </span>
                      {alert.metric && (
                        <span className="text-[11px] font-medium text-muted-foreground">{alert.metric}</span>
                      )}
                    </div>
                    <p className="mt-1 text-sm font-medium">{alert.title}</p>
                    <p className="mt-1 text-sm text-muted-foreground">{alert.message}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
